import * as React from "react";

import { AccessReportsWizardAccessCriteria, AccessReportsWizardProduct } from "@app/access-reports/shared";
import { isEqual } from "lodash";
import { QESButton } from "@app/shared/qes-standards";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";

interface EditSpecialtyNameProps {
  hideSidePanel: () => void;
  onSaveSpecialtyNameClick: (accessCriteriaId: number, accessCriteriaName: string) => void;
  product: AccessReportsWizardProduct;
  specialty: AccessReportsWizardAccessCriteria;
}

interface EditSpecialtyNameState {
  specialtyNameValue: string;
}

class EditSpecialtyName extends React.Component<EditSpecialtyNameProps, EditSpecialtyNameState> {
  constructor(props: EditSpecialtyNameProps) {
    super(props);

    this.state = {
      specialtyNameValue: "",
    };
  }

  public componentDidMount() {
    if (!!this.props.specialty) {
      this.setSpecialtyName();
    }
  }

  public componentDidUpdate(prevProps: EditSpecialtyNameProps) {
    if (!isEqual(prevProps.specialty, this.props.specialty)) {
      this.setSpecialtyName();
    }
  }

  public render() {
    const editSpecialtyNameErrorText: string = this.getEditSpecialtyNameErrorText();
    const missingPropsErrorText: string = this.getMissingPropsErrorText();

    return (
      <React.Fragment>
        {this.getEditSpecialtyNameContent(editSpecialtyNameErrorText, missingPropsErrorText)}

        <FlyoutFooter
          submitButton={
            <QESButton
              className="cursor-pointer ml-3"
              disabled={!!editSpecialtyNameErrorText || !!missingPropsErrorText}
              onClick={() => this.props.onSaveSpecialtyNameClick(this.props.specialty.id, this.state.specialtyNameValue)}
              qesTestId="access-reports-admin__edit-specialty-name--save"
              qesType="primary"
            >
              Save
            </QESButton>
          }
        />
      </React.Fragment>
    );
  }

  private getEditSpecialtyNameContent = (editSpecialtyNameErrorText: string, missingPropsErrorText: string): JSX.Element => {
    // only grant the user access to the edit specialty name input box
    // if a product, specialties, and the specific specialty whose name is to be edited exist
    if (!!missingPropsErrorText) {
      return (
        <div className="row">
          <div className="col-12">
            <p className="text-muted">
              <i className="text-danger fa fa-fw fa-exclamation-circle mr-1" />
              <b>Error: </b>
              {missingPropsErrorText}
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="row">
          <div className="col-12">
            <div className="form-group mb-3">
              <label className="mb-0">
                Specialty Name
              </label>
              <div className="input-group">
                <input
                  className="form-control"
                  id={`access-reports-admin__edit-specialty-name--input-${this.props.specialty?.id}`}
                  onChange={(e) => this.onSpecialtyNameInputChange(e)}
                  onKeyPress={(e) => this.onSpecialtyNameInputKeyPress(e, editSpecialtyNameErrorText)}
                  value={this.state.specialtyNameValue}
                />

              </div>
              <div className={`text-danger`}>
                {editSpecialtyNameErrorText} &nbsp;
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  private getEditSpecialtyNameErrorText = (): string => {
    if (!this.state.specialtyNameValue) {
      return "Please enter a specialty name";
    } else {
      const specialtyWithThisName: AccessReportsWizardAccessCriteria = !!this.props.product && this.props.product.accessCriterion.find(x => x.name === this.state.specialtyNameValue);

      if (!!specialtyWithThisName && this.props.specialty.id !== specialtyWithThisName.id) {
        return "A specialty with that name already exists";
      } else {
        return "";
      }
    }
  }

  private getMissingPropsErrorText = (): string => {
    if (!this.props.product) {
      return "No Product Found";
    } else if (!this.props.product.accessCriterion || !this.props.product.accessCriterion.length) {
      return "No Specialties Found";
    } else if (!this.props.specialty) {
      return "No Specialty to Edit";
    } else {
      return "";
    }
  }

  private onSpecialtyNameInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      specialtyNameValue: event.target.value,
    });
  }

  private onSpecialtyNameInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, editSpecialtyNameErrorText: string): void => {
    // allow for the user to save specialty name on enter key press
    // if the currently-input specialty name is valid
    if (event.key === "Enter" && !editSpecialtyNameErrorText) {
      event.preventDefault();
      this.props.onSaveSpecialtyNameClick(this.props.specialty.id, this.state.specialtyNameValue)
    }
  }

  private setSpecialtyName = (): void => {
    if (!!this.props.specialty) {
      this.setState({
        specialtyNameValue: this.props.specialty.name,
      });
    }
  }
}

export { EditSpecialtyName };
